import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ContactForm from 'src/pages/contact/components/contactForm/ContactForm';

import PublicLayout, {
  getPublicLayoutData,
} from 'src/components/layouts/PublicLayout/PublicLayout';
import MetaDescription from 'src/components/shared/Meta/MetaDescription';
import MetaNoIndex from 'src/components/shared/Meta/MetaNoIndex';
import MetaTitle from 'src/components/shared/Meta/MetaTitle';

import { getGarage } from 'src/services/garage/getGarage';
import { getHomeGarageSlugs } from 'src/services/garage/getHomeGarageSlugs';

import { logAPIError } from 'src/utils/SentryUtils';

export const getStaticPaths = async () => {
  if (process.env.DISABLE_ISR !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const garageSlugs = await getHomeGarageSlugs();
  const paths = garageSlugs.map((vehicleSlug) => {
    return {
      params: {
        id: vehicleSlug.subDomain,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  const garageSlug = typeof params?.id === 'string' ? params.id : '';

  try {
    const [publicLayoutData, garage] = await Promise.all([
      getPublicLayoutData(),
      getGarage(garageSlug),
    ]);

    return {
      revalidate: process.env.DISABLE_ISR !== 'true' ? 360 : undefined,
      props: {
        publicLayoutData,
        garage,
      },
    };
  } catch (error) {
    logAPIError(error);

    return {
      notFound: true,
      props: {},
    };
  }
};

const ContactPage: FunctionComponent<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ publicLayoutData, garage }) => {
  const router = useRouter();

  if (router.isFallback || !publicLayoutData || !garage) {
    return null;
  }

  return (
    <>
      <MetaNoIndex />
      <MetaTitle title={`Contactez le garage ${garage.name}`} />
      <MetaDescription description="Besoin d'aide ou des renseignements ? Contactez-nous en remplissant notre formulaire en ligne." />
      <PublicLayout data={publicLayoutData}>
        <GoogleReCaptchaProvider
          reCaptchaKey={process.env.NEXT_PUBLIC_RE_CAPTCHA_V3_ID ?? ''}
        >
          <ContactForm idNetworkBase={garage.garIdNetwork} />
        </GoogleReCaptchaProvider>
      </PublicLayout>
    </>
  );
};

export default ContactPage;
